import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companyUserService, CompanyUser, AddUserToCompanyRequest, UpdateUserRoleRequest } from '../services/companyUserService';
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from '../context/AuthContext';

interface CompanyUsersTabProps {
  companyId: string;
}

const CompanyUsersTab: React.FC<CompanyUsersTabProps> = ({ companyId }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [selectedUser, setSelectedUser] = useState<{ id: string; email: string } | null>(null);
  const [selectedRole, setSelectedRole] = useState<'ADMIN' | 'EMPLOYEE' | 'VIEWER'>('EMPLOYEE');

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['companyUsers', companyId],
    queryFn: () => companyUserService.getCompanyUsers(companyId),
    retry: false // Не повторяем запрос при ошибке 403
  });

  // Определяем роль текущего пользователя и является ли он админом
  const currentUserRole = useMemo(() => {
    if (!user || !users) return null;
    const currentUser = users.find(u => u.userId === user.id);
    return currentUser?.role || null;
  }, [user, users]);

  const isAdmin = currentUserRole === 'ADMIN';
  const isCurrentUser = (userId: string) => user?.id === userId;

  const { data: searchResults } = useQuery({
    queryKey: ['userSearch', companyId, searchEmail],
    queryFn: () => companyUserService.searchUsers(companyId, searchEmail),
    enabled: searchEmail.length >= 3
  });

  // Автоматически выбираем пользователя, если результат поиска один
  useEffect(() => {
    if (searchResults && searchResults.length === 1) {
      // Если выбранный пользователь не соответствует результату поиска, обновляем выбор
      if (!selectedUser || selectedUser.id !== searchResults[0].id) {
        setSelectedUser(searchResults[0]);
      }
    } else if (searchResults && searchResults.length === 0) {
      // Если результатов нет, сбрасываем выбор
      setSelectedUser(null);
    } else if (searchResults && searchResults.length > 1) {
      // Если несколько результатов, проверяем, соответствует ли выбранный пользователь введенному email
      if (selectedUser && selectedUser.email.toLowerCase() !== searchEmail.toLowerCase()) {
        // Если выбранный пользователь не соответствует поиску, сбрасываем выбор
        setSelectedUser(null);
      }
    }
  }, [searchResults, searchEmail, selectedUser]);

  const addUserMutation = useMutation({
    mutationFn: (data: AddUserToCompanyRequest) => companyUserService.addUserToCompany(companyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyUsers', companyId] });
      setIsAddModalOpen(false);
      setSearchEmail('');
      setSelectedUser(null);
    }
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateUserRoleRequest }) => 
      companyUserService.updateUserRole(companyId, userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyUsers', companyId] });
    }
  });

  const removeUserMutation = useMutation({
    mutationFn: (userId: string) => companyUserService.removeUserFromCompany(companyId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyUsers', companyId] });
    }
  });

  const handleAddUser = () => {
    // Можно добавить пользователя по email, даже если он не найден в поиске
    const emailToAdd = selectedUser ? selectedUser.email : searchEmail.trim();
    
    if (emailToAdd && emailToAdd.length > 0 && emailToAdd.includes('@')) {
      addUserMutation.mutate({
        userEmail: emailToAdd,
        role: selectedRole
      });
    }
  };

  const handleRoleChange = (userId: string, newRole: 'ADMIN' | 'EMPLOYEE' | 'VIEWER') => {
    updateRoleMutation.mutate({
      userId,
      data: { role: newRole }
    });
  };

  const handleRemoveUser = (userId: string) => {
    if (window.confirm(t('companies.users.confirmRemove') || 'Вы уверены, что хотите удалить этого пользователя?')) {
      removeUserMutation.mutate(userId);
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return t('companies.users.roles.ADMIN') || 'Администратор';
      case 'EMPLOYEE':
        return t('companies.users.roles.EMPLOYEE') || 'Сотрудник';
      case 'VIEWER':
        return t('companies.users.roles.VIEWER') || 'Просмотр';
      default:
        return role;
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-red-100 text-red-800';
      case 'EMPLOYEE':
        return 'bg-blue-100 text-blue-800';
      case 'VIEWER':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Если ошибка доступа (403), показываем сообщение вместо таблицы и кнопки
  if (error && (error as any)?.response?.status === 403) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {t('companies.users.accessDenied') || 'Доступ ограничен'}
          </h3>
          <p className="text-gray-600">
            {t('companies.users.onlyAdminAccess') || 'Только администратор компании имеет доступ к управлению пользователями.'}
          </p>
        </div>
      </div>
    );
  }

  // Если другая ошибка
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="text-center">
          <p className="text-red-600">
            {t('common.error') || 'Произошла ошибка'}
          </p>
        </div>
      </div>
    );
  }

  // Если данные успешно загружены (200), показываем таблицу и кнопку добавления
  return (
    <div className="space-y-6">
      {/* Заголовок с кнопкой добавления */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">
          {t('companies.users.title') || 'Пользователи компании'}
        </h2>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {t('companies.users.addUser') || '+ Добавить пользователя'}
        </button>
      </div>

      {/* Таблица пользователей */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('companies.users.email') || 'Email'}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('companies.users.role') || 'Роль'}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('companies.users.status') || 'Статус'}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.actions') || 'Действия'}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users && users.length > 0 ? (
              users.map((user) => {
                const activationUrl = user.activationToken 
                  ? `${window.location.origin}/activate/${user.activationToken}`
                  : null;
                
                return (
                  <tr key={user.userId}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.userEmail}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isCurrentUser(user.userId) && isAdmin ? (
                        <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadgeClass(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                      ) : (
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.userId, e.target.value as 'ADMIN' | 'EMPLOYEE' | 'VIEWER')}
                          className={`px-2 py-1 text-xs rounded-full border-0 focus:ring-2 focus:ring-blue-500 ${getRoleBadgeClass(user.role)}`}
                        >
                          <option value="ADMIN">{getRoleLabel('ADMIN')}</option>
                          <option value="EMPLOYEE">{getRoleLabel('EMPLOYEE')}</option>
                          <option value="VIEWER">{getRoleLabel('VIEWER')}</option>
                        </select>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.activated ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          {t('companies.users.activated') || 'Активирован'}
                        </span>
                      ) : (
                        <div className="flex flex-col gap-1">
                          <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                            {t('companies.users.notActivated') || 'Не активирован'}
                          </span>
                          {activationUrl && (
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="text"
                                readOnly
                                value={activationUrl}
                                className="text-xs px-2 py-1 border border-gray-300 rounded bg-gray-50 flex-1"
                                onClick={(e) => (e.target as HTMLInputElement).select()}
                              />
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(activationUrl);
                                  alert(t('companies.users.linkCopied') || 'Ссылка скопирована');
                                }}
                                className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                              >
                                {t('common.copy') || 'Копировать'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {isCurrentUser(user.userId) && isAdmin ? (
                        <span className="text-gray-400 text-xs">
                          {t('companies.users.cannotRemoveSelf') || 'Нельзя удалить себя'}
                        </span>
                      ) : (
                        <button
                          onClick={() => handleRemoveUser(user.userId)}
                          className="text-red-600 hover:text-red-900"
                        >
                          {t('common.delete') || 'Удалить'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                  {t('companies.users.noUsers') || 'Пользователи не найдены'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Модальное окно добавления пользователя */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('companies.users.addUser') || 'Добавить пользователя'}
              </h3>
              
              {/* Поиск пользователя */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companies.users.searchUser') || 'Email пользователя'}
                </label>
                <input
                  type="email"
                  value={searchEmail}
                  onChange={(e) => {
                    setSearchEmail(e.target.value);
                    // Сбрасываем выбранного пользователя при изменении email
                    if (selectedUser && selectedUser.email !== e.target.value) {
                      setSelectedUser(null);
                    }
                  }}
                  placeholder={t('companies.users.searchPlaceholder') || 'Введите email...'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('companies.users.emailHint') || 'Можно добавить существующего пользователя или создать нового'}
                </p>
                
                {/* Результаты поиска */}
                {searchEmail.length >= 3 && (
                  <>
                    {searchResults && searchResults.length > 0 ? (
                      <div className="mt-2 border border-gray-200 rounded-md max-h-40 overflow-y-auto">
                        {searchResults.map((user) => (
                          <div
                            key={user.id}
                            onClick={() => {
                              setSelectedUser(user);
                              setSearchEmail(user.email);
                            }}
                            className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                              selectedUser?.id === user.id ? 'bg-blue-100' : ''
                            }`}
                          >
                            {user.email}
                          </div>
                        ))}
                      </div>
                    ) : searchResults && searchResults.length === 0 ? (
                      <div className="mt-2 text-sm text-gray-500 p-2 bg-yellow-50 rounded">
                        {t('companies.users.noUsersFound') || 'Пользователь не найден. Он будет создан при добавлении.'}
                      </div>
                    ) : null}
                    {selectedUser && (
                      <div className="mt-2 text-sm text-green-600 p-2 bg-green-50 rounded">
                        {t('companies.users.selectedUser') || 'Выбран'}: {selectedUser.email}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Выбор роли */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companies.users.role') || 'Роль'}
                </label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value as 'ADMIN' | 'EMPLOYEE' | 'VIEWER')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ADMIN">{getRoleLabel('ADMIN')}</option>
                  <option value="EMPLOYEE">{getRoleLabel('EMPLOYEE')}</option>
                  <option value="VIEWER">{getRoleLabel('VIEWER')}</option>
                </select>
              </div>

              {/* Кнопки */}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setIsAddModalOpen(false);
                    setSearchEmail('');
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  {t('common.cancel') || 'Отмена'}
                </button>
                <button
                  onClick={handleAddUser}
                  disabled={(!selectedUser && (!searchEmail || searchEmail.trim().length < 3 || !searchEmail.includes('@'))) || addUserMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {addUserMutation.isPending 
                    ? (t('common.adding') || 'Добавление...') 
                    : (t('common.add') || 'Добавить')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyUsersTab;

